import { SearchPlanService } from '../../../services/searchPlan/searchPlanService.service';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { catchError, finalize, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { AuthDataService } from '../../../services/auth/auth-data.service';
import { ISearchContractResponse } from '../../../interfaces/ISearchContract';
import { passwordMatchValidator } from '../../../utils/validators';
import { Router } from '@angular/router';
import { IMessage } from '../../../services/messages/messages.interface';
import { MessagesService } from '../../../services/messages/messages.service';
import { ICreateAccountContractDetailsRequest, ICreateAccountRequest } from '../../../interfaces/IAuth';
import { emailValidator } from '../../../validators/email/email.validator';
import { APP_ROUTES } from '../../../utils/route.constant';
import { Ga4Service } from '../../../services/ga4/ga4.service';
import { ACC_LOGGEDOUT, CREATE_ACCOUNT, CREATE_ACCOUNT_REGISTRATION_FLOW, ALL_PAGES, EMAIL_ALREADY_EXISTS, ERR_EVENT, SR_PM_INIT_FLOW_CONFIRMATION_PAGE, REGISTRATION_EMAIL_ALREADY_EXISTS_TOAST_MESSAGE, REGISTRATION_EMAIL_ALREADY_EXISTS_ERROR_MESSAGE, REGISTRATION_EMAIL_ALREADY_EXISTS_LINKS } from '../../../services/ga4/ga4.constant';
import { IContractData } from '../../../interfaces/IJob';
import { trackByIndex } from '../../../utils/helper';
import { DataService } from '../../../services/data/data.service';

@Component({
  selector: 'app-create-account-shared',
  templateUrl: './create-account-shared.component.html',
  styleUrl: './create-account-shared.component.scss'
})
export class CreateAccountSharedComponent implements OnInit, OnDestroy {
  cleaner$ = new Subject<void>();
  registerForm!: FormGroup;
  plan!: ISearchContractResponse;
  planMultiple!: ISearchContractResponse[];
  loading: boolean = false
  isCheckingEmail: boolean = false;
  hasMinLength = false;
  hasLowercase = false;
  hasUppercase = false;
  hasNumber = false;
  hasSpecialChar = false;
  toastTitle = 'An account with this email already exists';
  toastIcon = 'pi pi-exclamation-triangle text-orange-900';
  toastbg = 'text-orange-900 bg-orange-100 border-orange-200 font-normal';
  @Input() isVivr: boolean = false; 
  @Input() contractData!: IContractData;
  @Input() funnelName: string = ACC_LOGGEDOUT;
  @Input() ga4Location: string = SR_PM_INIT_FLOW_CONFIRMATION_PAGE;
  trackByIndex = trackByIndex;
  constructor(private searchPlanService: SearchPlanService,
    private authDataService: AuthDataService, private router: Router,
    private messages: MessagesService, private gaService: Ga4Service, private dataService: DataService) { }

  ngOnInit(): void {
    this.initializeForm();
    const selectedPlan = this.searchPlanService.selectedPlan$.value;
    const selectedPlanMultiple = this.searchPlanService.selectedPlanMultiple$.value;
    if (selectedPlanMultiple?.length) {
      this.planMultiple = selectedPlanMultiple;
    }

    this.registerForm.get('password')?.valueChanges
    .pipe(takeUntil(this.cleaner$))
    .subscribe((value) => {
      this.validatePassword(value);
    });
  }

  ngOnDestroy(): void {
    this.cleaner$.next();
    this.cleaner$.complete();
  }

  initializeForm(): void{
    this.registerForm = new FormGroup(
      {
        email: new FormControl('', {
          validators: [Validators.required, emailValidator()],
          asyncValidators: this.emailExistsValidator.bind(this),
          updateOn: 'blur',
        }),
        password: new FormControl('', [
          Validators.required,
          Validators.minLength(8), // Minimum 8 characters
          Validators.pattern(/(?=.*[a-z])/) /* At least one lowercase */,
          Validators.pattern(/(?=.*[A-Z])/) /* At least one uppercase */,
          Validators.pattern(/(?=.*\d)/) /* At least one number */,
          Validators.pattern(/(?=.*[@$!%*?&])/) /* At least one special character */,
        ]),
        confirmPassword: new FormControl('', [Validators.required]),
      },
      { validators: passwordMatchValidator } // Custom validator for password matching
    );
  }

  validatePassword(password: string): void {
    this.hasMinLength = password.length >= 8;
    this.hasLowercase = /[a-z]/.test(password);
    this.hasUppercase = /[A-Z]/.test(password);
    this.hasNumber = /\d/.test(password);
    this.hasSpecialChar = /[@$!%*?&]/.test(password);
  }

  createAccount(): void {
    if (this.registerForm.invalid) { return; }
    if (!this.isVivr && !this.planMultiple?.length) { return; }
    this.loading = true;
    const { email, password } = this.registerForm.value;
    let name: string;
    let securityToken: string;
    let contractDetails: ICreateAccountContractDetailsRequest[] = [];
    if (this.isVivr) {
      name =
        `${this.contractData.baseContract.primaryCustomer.firstName || ''} ${this.contractData.baseContract.primaryCustomer.lastName || ''
          }`.trim() || 'Customer';

      securityToken = this.dataService.vivrAccessToken$.getValue() || '';

      contractDetails = [
        {
          clientId: this.contractData.baseContract.clientId,
          contract: this.contractData.baseContract.contractNumber,
          coverageGroup: this.contractData.baseContract.coverageGroup,
          clientSpecificProductName:
            this.contractData.baseContract.filedClientSpecificProductName,
        },
      ];
    }else{
      name = this.planMultiple[0].personFirstName + ' ' + this.planMultiple[0].personLastName;
      securityToken =this.planMultiple[0].securityToken;
      contractDetails = this.planMultiple.map((plan) => {return {
        clientId: plan.clientId,
        contract: plan.contractNumber,
        coverageGroup: plan.coverageGroup,
        clientSpecificProductName: plan.filedClientSpecificProductName
      }});
    }
    const signUpRequest: ICreateAccountRequest = {
      password,
      email,
      name,
      securityToken,
      contractDetails,
    };
    this.gaService.buttonClickEvent(this.isVivr ? this.ga4Location : CREATE_ACCOUNT_REGISTRATION_FLOW, CREATE_ACCOUNT);

    this.authDataService.createAcccount(signUpRequest)
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          if (response.statusCode === 201) {
            // Navigate to confirmation screen
            this.router.navigate([APP_ROUTES.CREATE_ACCOUNT_CONFIRMATION]);
          }else{
            const errorMessage: IMessage = {
              severity: 'error',
              text: response.message || '',
              icon: 'pi pi-ban'
            };
            this.messages.addMessages(errorMessage);
            this.gaService.messageEvent(ERR_EVENT, errorMessage.text, ALL_PAGES);
          }
        },
        error: (err) => {
          this.loading = false;
          const errorMessage: IMessage = {
            severity: 'error',
            text: err.message,
            icon: 'pi pi-ban'
          };
          this.messages.addMessages(errorMessage);
          this.gaService.messageEvent(ERR_EVENT, errorMessage.text, ALL_PAGES);
        }
      });
  }

  private emailExistsValidator(control: AbstractControl): Observable<ValidationErrors | null> {
    this.isCheckingEmail = true;

    return this.authDataService.checkEmail(control.value)
    .pipe(
      takeUntil(this.cleaner$),
      map((response) => {
        if (response.successFlag === 'Y' && response.userAlreadyExists === true) {
          this.gaService.messageEvent(ERR_EVENT, EMAIL_ALREADY_EXISTS, ALL_PAGES);
          return { emailExists: true };
        }
        return null;
      }),
      catchError(() => of(null)),
      finalize(() => { this.isCheckingEmail = false; })
    );
  }

  navigateToLogin(location: string = ''): void {
    this.gaService.buttonClickEvent(this.buttonLocation(location), 'login_instead');
    this.dataService.enteredEmail$.next(this.registerForm.get('email')?.value || '');
    this.router.navigate([APP_ROUTES.LOGIN]);
  }

  navigateToForgotPassword(location: string = ''): void {
    this.gaService.buttonClickEvent(this.buttonLocation(location), 'forgot_password');
    this.dataService.enteredEmail$.next(this.registerForm.get('email')?.value || '');
    this.router.navigate([APP_ROUTES.PASSWORD_RESET]);
  }

  buttonLocation(location: string): string {
    switch (location) {
      case 'toast':
        return REGISTRATION_EMAIL_ALREADY_EXISTS_TOAST_MESSAGE;
      case 'error':
        return REGISTRATION_EMAIL_ALREADY_EXISTS_ERROR_MESSAGE;
      case 'links':
        return REGISTRATION_EMAIL_ALREADY_EXISTS_LINKS;
      default:
        return CREATE_ACCOUNT_REGISTRATION_FLOW;
    }
  }

  back(): void {
    this.router.navigate(['/registration']);
  }

  get cardHeader(): string {
    return this.isVivr ? "Track your service in one spot": "Great! Let's finish creating your account";
  }

  get cardSubtitle(): string {
    return this.isVivr ? "Create an account to follow your request and see updates." : "Choose an email address and password for your account.";
  }
}
